import { useState, useEffect, useCallback } from "react";
import Sidebar from "../components/layout/Sidebar";
import HostingNav from "../components/layout/HostingNav";
import PageHeader from "../components/PageHeader";
import { WebsiteGraph, WebsiteTable, AddWebsiteSidepanel, LinkQueuePanel } from "../features/websites";
import WebsiteTasksPanel from "../features/websites/WebsiteTasksPanel";
import GraphSettingsPanel from "../features/websites/GraphSettingsPanel";
import { Button, IconButton, Tabs, Badge } from "../components/ui";
import { supabase } from "../lib/supabase";
import { useUser } from "../context/UserContext";
import { WEBSITE_COLUMNS } from "../lib/supabase/websites";

type TabId = "graph" | "table" | "tasks" | "link-queue";

const TAB_NAMES = ["Graph", "Table", "Tasks", "Link Queue"];

const TAB_BY_NAME: Record<string, TabId> = {
  Graph: "graph",
  Table: "table",
  Tasks: "tasks",
  "Link Queue": "link-queue",
};

const NAME_BY_TAB: Record<TabId, string> = {
  graph: "Graph",
  table: "Table",
  tasks: "Tasks",
  "link-queue": "Link Queue",
};

const SEARCH_PLACEHOLDERS: Record<TabId, string | undefined> = {
  graph: undefined,
  table: "Search websites...",
  tasks: "Search tasks...",
  "link-queue": "Search queue...",
};

export default function PrivateNetwork() {
  const { user } = useUser();
  const role = user?.role?.trim?.().toLowerCase?.();
  const hasFullAccess = role === 'admin' || role === 'developer' || role === 'authenticated' || role === 'agency_owner';
  const [selectedTab, setSelectedTab] = useState<TabId>("graph");
  const [forceRefresh, setForceRefresh] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedColor, setSelectedColor] = useState<string>('');
  const [isExporting, setIsExporting] = useState(false);
  const [isAddWebsiteSidepanelOpen, setIsAddWebsiteSidepanelOpen] = useState(false);
  const [isSettingsPanelOpen, setIsSettingsPanelOpen] = useState(false);
  const [isAddingLink, setIsAddingLink] = useState(false);
  const [siteCount, setSiteCount] = useState<number | undefined>(undefined);
  const [pendingLinks, setPendingLinks] = useState<number>(0);
  const [forceSettings, setForceSettings] = useState({
    linkDistance: 90,
    attractionStrength: 0.1,
    chargeStrength: -120,
    nodeSize: 6,
    zoomLevel: 2.5,
    particleSpeed: 0.005,
    particleCount: 2,
  });

  const handleWebsitesUpdated = () => {
    // Force refresh of data
    setForceRefresh((prev) => prev + 1);
  };

  const handleAddWebsiteClick = () => {
    setIsAddWebsiteSidepanelOpen(true);
  };

  const handleSettingsChange = (setting: keyof typeof forceSettings, value: number) => {
    setForceSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  const handleResetSettings = () => {
    setForceSettings({
      linkDistance: 90,
      attractionStrength: 0.1,
      chargeStrength: -120,
      nodeSize: 6,
      zoomLevel: 2.5,
      particleSpeed: 0.005,
      particleCount: 2,
    });
  };

  // Header counts: total sites + pending link-queue items
  const fetchCounts = useCallback(async () => {
    try {
      const [sitesRes, pendingRes] = await Promise.all([
        supabase.from('websites').select('*', { count: 'exact', head: true }),
        supabase.from('link_queue').select('*', { count: 'exact', head: true }).eq('status', 'pending'),
      ]);
      if (typeof sitesRes.count === 'number') setSiteCount(sitesRes.count);
      if (typeof pendingRes.count === 'number') setPendingLinks(pendingRes.count);
    } catch (err) {
      console.error('Error fetching network counts:', err);
    }
  }, []);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts, forceRefresh]);

  const handleExportCSV = async () => {
    try {
      setIsExporting(true);

      // Fetch all websites from Supabase
      const { data: allWebsites, error } = await supabase
        .from('websites')
        .select(WEBSITE_COLUMNS)
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (!allWebsites || allWebsites.length === 0) {
        throw new Error('No websites to export');
      }

      // Format date helper
      const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      };

      // Define CSV headers
      const headers = [
        'URL',
        'Title',
        'Category',
        'Color',
        'Build Status',
        'Deployment Status',
        'Indexing Status',
        'DNS Host',
        'Registrar',
        'Hosting Provider',
        'Hosting Location',
        'IP Address',
        'Frontend Platform',
        'Frontend Theme',
        'Search Console Access',
        'Last Content Update',
        'Created At'
      ];

      // Convert data to CSV format
      const csvContent = [
        headers.join(','),
        ...allWebsites.map(website => [
          `"${website.url || ''}"`,
          `"${website.title || ''}"`,
          `"${website.category || ''}"`,
          `"${website.color || ''}"`,
          `"${website.build_status || ''}"`,
          `"${website.deployment_status || ''}"`,
          `"${website.indexing_status || ''}"`,
          `"${website.dns_host || ''}"`,
          `"${website.registrar || ''}"`,
          `"${website.hosting_provider || ''}"`,
          `"${website.hosting_location || ''}"`,
          `"${website.ip_address || ''}"`,
          `"${website.frontend_platform || ''}"`,
          `"${website.frontend_theme || ''}"`,
          `"${website.search_console_access || ''}"`,
          `"${website.last_content_update ? formatDate(website.last_content_update) : ''}"`,
          `"${website.created_at ? formatDate(website.created_at) : ''}"`
        ].join(','))
      ].join('\n');

      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');

      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `websites-export-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Error exporting websites:', err);
      // You could add error state here if needed
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Sidebar />

      <main className="px-4 md:px-8 pb-8 md:ml-[72px]">
        <PageHeader
          title="Private Network"
          count={siteCount}
          searchPlaceholder={SEARCH_PLACEHOLDERS[selectedTab]}
          searchValue={SEARCH_PLACEHOLDERS[selectedTab] ? searchTerm : undefined}
          onSearchChange={SEARCH_PLACEHOLDERS[selectedTab] ? setSearchTerm : undefined}
          actions={
            <span style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginLeft: '6px' }}>
              {selectedTab === 'graph' && (
                <Button
                  size="sm"
                  variant={isAddingLink ? 'primary' : 'secondary'}
                  icon={isAddingLink ? 'x' : 'link'}
                  onClick={() => setIsAddingLink(!isAddingLink)}
                >
                  {isAddingLink ? 'Cancel Link' : 'Add Link'}
                </Button>
              )}
              {selectedTab === 'table' && (
                <Button size="sm" variant="secondary" onClick={handleExportCSV} disabled={isExporting}>
                  {isExporting ? 'Exporting...' : 'Export CSV'}
                </Button>
              )}
              <Button size="sm" icon="plus" onClick={handleAddWebsiteClick}>
                Add Website
              </Button>
              {selectedTab === 'graph' && (
                <IconButton
                  size="sm"
                  variant="outline"
                  icon="settings"
                  label="Graph Settings"
                  onClick={() => setIsSettingsPanelOpen(true)}
                />
              )}
            </span>
          }
        />

        {/* Staff reach this through Hosting's submenu; client users only have this screen */}
        {hasFullAccess && <HostingNav />}

        {/* Tabs + pending-links signal */}
        {/* Phones: the pending badge wraps under the tabs, and the tabs
            scroll sideways rather than push the page wider */}
        <div className="flex flex-wrap md:flex-nowrap items-end justify-between" style={{ marginBottom: 'var(--space-5)', rowGap: '8px' }}>
          {/* 1px bottom padding holds the active underline (bottom: -1px) so
              the scroller doesn't pick up a sliver of vertical overflow */}
          <div className="scrollbar-hidden" style={{ flex: '1 1 100%', minWidth: 0, overflowX: 'auto', paddingBottom: '1px' }}>
            <Tabs
              tabs={TAB_NAMES}
              active={NAME_BY_TAB[selectedTab]}
              onChange={(tab) => setSelectedTab(TAB_BY_NAME[tab] ?? 'graph')}
              style={{ minWidth: 'max-content' }}
            />
          </div>
          {pendingLinks > 0 && (
            <span className="md:mb-[10px] md:ml-[12px]" style={{ display: 'inline-flex', flexShrink: 0 }}>
              <Badge tone="warn" dot>
                {pendingLinks} links pending
              </Badge>
            </span>
          )}
        </div>

        {/* Tasks Panel */}
        {selectedTab === "tasks" && (
          <div
            className="p-4 md:p-[24px]"
            style={{
              background: 'var(--surface-card)',
              border: '1px solid var(--border-hairline)',
              borderRadius: 'var(--radius-lg)',
              boxShadow: 'var(--shadow-rest)',
              marginBottom: 'var(--space-6)',
            }}
          >
            <WebsiteTasksPanel searchTerm={searchTerm} />
          </div>
        )}

        {/* Link Queue Panel */}
        {selectedTab === "link-queue" && (
          <div style={{ marginBottom: 'var(--space-6)' }}>
            <LinkQueuePanel />
          </div>
        )}

        {selectedTab === "table" && (
          <div style={{ marginBottom: 'var(--space-6)' }}>
            <WebsiteTable
              searchTerm={searchTerm}
              selectedColor={selectedColor}
              onColorFilterChange={setSelectedColor}
              onWebsitesUpdated={handleWebsitesUpdated}
              forceRefresh={forceRefresh}
            />
          </div>
        )}

        {selectedTab === "graph" && (
          // dvh, not vh: iOS vh is the tallest viewport (toolbars hidden), so
          // the graph ran under the bottom nav. Phones also subtract the
          // notch, home indicator and bottom nav.
          <div
            className="h-[calc(100dvh_-_var(--sat)_-_var(--sab)_-_var(--bottom-nav-h)_-_225px)] md:h-[calc(100dvh_-_280px)] min-h-[360px] md:min-h-[420px]"
            style={{
              position: 'relative',
              marginBottom: 'var(--space-6)',
              background: 'var(--surface-card)',
              border: '1px solid var(--border-hairline)',
              borderRadius: 'var(--radius-lg)',
              boxShadow: 'var(--shadow-rest)',
              overflow: 'hidden',
            }}
          >
            <WebsiteGraph
              onWebsitesUpdated={handleWebsitesUpdated}
              forceRefresh={forceRefresh}
              isAddingLink={isAddingLink}
              setIsAddingLink={setIsAddingLink}
              forceSettings={forceSettings}
            />
          </div>
        )}

        {/* Add Website slide-over (available from every tab) */}
        <AddWebsiteSidepanel
          isOpen={isAddWebsiteSidepanelOpen}
          onClose={() => setIsAddWebsiteSidepanelOpen(false)}
          onWebsiteAdded={handleWebsitesUpdated}
        />

        {/* Graph force settings */}
        <GraphSettingsPanel
          isOpen={isSettingsPanelOpen}
          setIsOpen={setIsSettingsPanelOpen}
          settings={forceSettings}
          onSettingsChange={handleSettingsChange}
          onReset={handleResetSettings}
        />
      </main>
    </div>
  );
}
